// Phase orchestrator. The funnel is a small linear state machine:
//   questionnaire → result → account → checkEmail → getApp
// with one honest side exit, account → existingAccount, for an email that already
// has an Expy account (the server sends no email then, so "check your email" would lie).
// App holds the cross-phase state (answers + coverage) and streams auto-height +
// non-PHI conversion events to a host page when embedded.
import { useEffect, useState } from 'react';
import { loadClaimLink, saveClaimLink, type Answers } from './lib/session';
import type { Coverage } from './lib/api';
import { observeHeight, postEvent } from './lib/postMessage';
import { Questionnaire } from './screens/Questionnaire';
import { Result } from './screens/Result';
import { Account } from './screens/Account';
import { CheckEmail } from './screens/CheckEmail';
import { GetTheApp } from './screens/GetTheApp';
import { ExistingAccount, type ExistingReason } from './screens/ExistingAccount';

type Phase = 'questionnaire' | 'result' | 'account' | 'existingAccount' | 'checkEmail' | 'getApp';

export default function App() {
  const [phase, setPhase] = useState<Phase>('questionnaire');
  const [answers, setAnswers] = useState<Answers>({});
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [claimLink, setClaimLink] = useState<string | undefined>();
  const [existingReason, setExistingReason] = useState<ExistingReason>('existing');

  // Auto-height for the Elementor iframe (no-op on the canonical hosted route).
  useEffect(() => observeHeight(), []);
  // Scroll to top on phase change so a taller previous screen doesn't leave us mid-page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [phase]);

  return (
    <>
      {phase === 'questionnaire' && (
        <Questionnaire
          onComplete={(a, cov) => {
            setAnswers(a);
            setCoverage(cov);
            postEvent('questionnaire_complete');
            setPhase('result');
          }}
        />
      )}
      {phase === 'result' && <Result answers={answers} coverage={coverage} onStart={() => setPhase('account')} />}
      {phase === 'account' && (
        <Account
          answers={answers}
          coverage={coverage}
          onCreated={(link) => {
            setClaimLink(link);
            // Persisted too: the Stripe checkout return is a full page load, and
            // the success page still wants to show the sign-in QR.
            if (link) saveClaimLink(link);
            postEvent('account_created');
            setPhase('checkEmail');
          }}
          onAlreadyConverted={() => {
            // Same session, second submit (back button, reload): the account and its
            // invite email already exist, so replay the hand-off without counting a
            // second conversion. The QR comes back from the link persisted at creation.
            setClaimLink((current) => current || loadClaimLink());
            setPhase('checkEmail');
          }}
          onExisting={(reason) => {
            setExistingReason(reason);
            setPhase('existingAccount');
          }}
        />
      )}
      {phase === 'existingAccount' && (
        <ExistingAccount answers={answers} reason={existingReason} onBack={() => setPhase('account')} />
      )}
      {phase === 'checkEmail' && <CheckEmail answers={answers} onContinue={() => setPhase('getApp')} />}
      {phase === 'getApp' && (
        <GetTheApp
          answers={answers}
          claimLink={claimLink}
          // Card-free trial: subscribing here is optional. Only offered where the
          // rail is launched (US) or when the country signal is missing; the
          // server enforces the same policy regardless.
          offerSubscription={!answers.country || answers.country === 'US'}
        />
      )}
    </>
  );
}
